/* Recommendations ticker: requestAnimationFrame + scrollLeft loop with cloned sets.
   Pauses on hover / focus / user scroll, supports drag, prev/next, reduced motion. */
(function () {
  if (customElements.get('yv-reco-ticker')) return;

  class YvRecoTicker extends HTMLElement {
    constructor() {
      super();
      this.tick = this.tick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
    }

    connectedCallback() {
      this.wrapper = this.closest('.srt');
      if (this.dataset.url) {
        this.load();
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.destroy();
    }

    async load(attempt = 0) {
      let ok = false;
      try {
        const res = await fetch(this.dataset.url, { headers: { Accept: 'text/html' } });
        const html = res.ok ? await res.text() : '';
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-srt-content]');
        const target = this.querySelector('[data-srt-content]');
        if (fresh && target && fresh.querySelector('[data-srt-track]')) {
          target.innerHTML = fresh.innerHTML;
          ok = true;
        } else if (html.trim() && fresh) {
          ok = true; // rendered, genuinely no recommendations
        }
      } catch (e) {
        console.error('[yv ticker]', e);
      }
      if (!ok && attempt < 2) {
        setTimeout(() => this.load(attempt + 1), 1500 * (attempt + 1));
        return;
      }
      this.init();
    }

    init() {
      this.track = this.querySelector('[data-srt-track]');
      if (!this.track) return this.setEmpty();

      const pid = this.dataset.productId;
      if (pid) {
        this.track.querySelectorAll('.srt__card').forEach((card) => {
          if (card.dataset.productId === pid) card.remove();
        });
      }

      this.items = Array.from(this.track.children);
      if (!this.items.length) return this.setEmpty();

      if (this.wrapper) this.wrapper.classList.remove('srt--loading', 'srt--empty');

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.speed = reduced ? 0 : parseFloat(this.dataset.speed) || 0;
      this.pauseHover = this.dataset.pauseHover === 'true';
      this.hovering = false;
      this.focused = false;
      this.userActive = false;
      this.visible = true;
      this.drag = null;
      this.last = 0;

      this.buildClones();
      this.pos = this.setWidth;
      this.track.scrollLeft = this.pos;

      this.bind();
      this.raf = requestAnimationFrame(this.tick);
    }

    setEmpty() {
      if (this.wrapper) {
        this.wrapper.classList.remove('srt--loading');
        this.wrapper.classList.add('srt--empty');
      }
    }

    buildClones() {
      this.track.querySelectorAll('[data-srt-clone]').forEach((c) => c.remove());
      const makeSet = () => {
        const frag = document.createDocumentFragment();
        this.items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('data-srt-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
          frag.appendChild(clone);
        });
        this.track.appendChild(frag);
      };

      makeSet();
      const firstClone = this.track.querySelector('[data-srt-clone]');
      this.setWidth = Math.max(1, firstClone.offsetLeft - this.items[0].offsetLeft);
      const viewport = this.track.clientWidth || window.innerWidth;
      const needed = Math.min(12, 3 + Math.ceil(viewport / this.setWidth));
      for (let i = 2; i < needed; i++) makeSet();

      this.step = this.items.length > 1 ? this.items[1].offsetLeft - this.items[0].offsetLeft : this.setWidth;
      this.width = this.track.clientWidth;
    }

    bind() {
      const t = this.track;
      this.listeners = [];
      const on = (el, ev, fn, opts) => {
        el.addEventListener(ev, fn, opts);
        this.listeners.push([el, ev, fn, opts]);
      };

      on(t, 'scroll', this.onScroll, { passive: true });
      on(t, 'touchstart', () => this.setActive(), { passive: true });
      on(t, 'wheel', () => this.setActive(), { passive: true });
      on(t, 'keydown', () => this.setActive());
      on(t, 'dragstart', (e) => e.preventDefault());
      on(t, 'pointerdown', (e) => this.onPointerDown(e));
      on(
        t,
        'click',
        (e) => {
          if (this.justDragged) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );

      const viewport = this.querySelector('.srt__viewport');
      const canHover = window.matchMedia('(hover: hover)').matches;
      if (viewport && canHover) {
        on(viewport, 'mouseenter', () => {
          this.hovering = true;
        });
        on(viewport, 'mouseleave', () => {
          this.hovering = false;
        });
      }
      on(this, 'focusin', (e) => {
        let fv = true;
        try {
          fv = e.target.matches(':focus-visible');
        } catch (err) {}
        this.focused = fv;
      });
      on(this, 'focusout', () => {
        this.focused = this.contains(document.activeElement);
      });

      const prev = this.querySelector('[data-srt-prev]');
      const next = this.querySelector('[data-srt-next]');
      if (prev) on(prev, 'click', () => this.go(-1));
      if (next) on(next, 'click', () => this.go(1));

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
        });
        this.io.observe(this);
      }

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => {
          if (!this.track || Math.abs(this.track.clientWidth - this.width) < 2) return;
          const ratio = (this.pos - this.setWidth) / this.setWidth;
          this.buildClones();
          this.pos = this.setWidth + ratio * this.setWidth;
          this.track.scrollLeft = this.pos;
        });
        this.ro.observe(this.track);
      }
    }

    destroy() {
      cancelAnimationFrame(this.raf);
      clearTimeout(this.activeTimer);
      if (this.listeners) this.listeners.forEach(([el, ev, fn, opts]) => el.removeEventListener(ev, fn, opts));
      this.listeners = [];
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
    }

    isPaused() {
      return (
        this.speed <= 0 ||
        !this.visible ||
        this.userActive ||
        this.drag ||
        this.focused ||
        (this.pauseHover && this.hovering)
      );
    }

    tick(time) {
      this.raf = requestAnimationFrame(this.tick);
      const dt = this.last ? Math.min(time - this.last, 50) : 16;
      this.last = time;
      if (this.isPaused()) return;
      this.pos += this.speed * dt * 0.06;
      this.wrap();
      this.track.scrollLeft = this.pos;
    }

    wrap() {
      const sw = this.setWidth;
      let delta = 0;
      if (this.pos >= sw * 2) delta = -sw;
      else if (this.pos < sw * 0.5) delta = sw;
      if (delta) {
        this.pos += delta;
        if (this.drag) this.drag.start += delta;
      }
      return delta;
    }

    onScroll() {
      if (!this.userActive && !this.drag) return;
      this.pos = this.track.scrollLeft;
      if (this.wrap()) this.track.scrollLeft = this.pos;
      if (this.userActive) this.setActive();
    }

    setActive() {
      this.userActive = true;
      clearTimeout(this.activeTimer);
      this.activeTimer = setTimeout(() => {
        this.userActive = false;
        this.pos = this.track.scrollLeft;
      }, 1200);
    }

    go(dir) {
      this.setActive();
      this.track.scrollBy({ left: dir * this.step, behavior: 'smooth' });
    }

    onPointerDown(e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      this.drag = { x: e.clientX, start: this.track.scrollLeft, moved: false };
      this.setActive();
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
    }

    onPointerMove(e) {
      if (!this.drag) return;
      const dx = e.clientX - this.drag.x;
      if (!this.drag.moved && Math.abs(dx) > 5) {
        this.drag.moved = true;
        this.track.classList.add('is-dragging');
      }
      if (this.drag.moved) {
        this.track.scrollLeft = this.drag.start - dx;
        this.setActive();
      }
    }

    onPointerUp() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      if (!this.drag) return;
      const moved = this.drag.moved;
      this.drag = null;
      this.pos = this.track.scrollLeft;
      if (moved) {
        this.justDragged = true;
        setTimeout(() => {
          this.justDragged = false;
          this.track.classList.remove('is-dragging');
        }, 0);
      }
    }
  }

  customElements.define('yv-reco-ticker', YvRecoTicker);
})();
